import type {Schema} from 'yup';

// A getter, not a Storage: reading the sessionStorage property itself throws
// when cookies are blocked, so resolving it eagerly would break the bundle.
export class StoredJsonEntry<T> {
  constructor(
    private readonly getStorage: () => Storage,
    private readonly key: string,
    private readonly schema: Schema<T>,
  ) {}

  get(): T | null {
    try {
      const storage = this.getStorage();
      const raw = storage.getItem(this.key);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as unknown;
      // Strict, or yup coerces wrong-typed fields into passing.
      if (!this.schema.isValidSync(parsed, {strict: true})) {
        storage.removeItem(this.key);
        return null;
      }

      return parsed;
    } catch {
      this.clear();
      return null;
    }
  }

  set(value: T): void {
    try {
      this.getStorage().setItem(this.key, JSON.stringify(value));
    } catch {
      // storage disabled or quota exceeded; callers degrade to the OTP flow
    }
  }

  clear(): void {
    try {
      this.getStorage().removeItem(this.key);
    } catch {
      // ignore storage access failures
    }
  }

  // A slow failing request must not delete an entry stored since it started.
  clearIf(matches: (stored: T) => boolean): void {
    const stored = this.get();
    if (!stored || matches(stored)) {
      this.clear();
    }
  }
}
